import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import './MultiSelectDropdown.css';

interface MultiSelectDropdownProps {
    label: string;
    options: string[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
}

export function MultiSelectDropdown({ label, options, selected, onChange, placeholder = "Select..." }: MultiSelectDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleOption = (option: string) => {
        const isSelected = selected.includes(option);
        if (isSelected) {
            onChange(selected.filter(o => o !== option));
        } else {
            onChange([...selected, option]);
        }
    };

    const isAllSelected = selected.length === options.length && options.length > 0;

    const toggleAll = () => {
        if (isAllSelected) {
            onChange([]);
        } else {
            onChange([...options]);
        }
    };

    const displayValue = selected.length === 0
        ? placeholder
        : selected.length === options.length
            ? "All Selected"
            : `${selected.length} Selected`;

    return (
        <div className="multi-select-container" ref={dropdownRef}>
            <span className="filter-label">{label}:</span>
            <div className="multi-select-dropdown" onClick={() => setIsOpen(!isOpen)}>
                <div className={`multi-select-trigger ${isOpen ? 'active' : ''}`}>
                    <span className="multi-select-text">{displayValue}</span>
                    <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${isOpen ? 'open' : ''}`} />
                </div>
                {isOpen && (
                    <div className="multi-select-options">
                        <div className="multi-select-option all-option" onClick={(e) => { e.stopPropagation(); toggleAll(); }}>
                            <FontAwesomeIcon icon={isAllSelected ? faCheckSquare : faSquare} className="checkbox-icon" />
                            <span>Select All</span>
                        </div>
                        <div className="options-divider" />
                        {options.map(option => (
                            <div
                                key={option}
                                className="multi-select-option"
                                onClick={(e) => { e.stopPropagation(); toggleOption(option); }}
                            >
                                <FontAwesomeIcon
                                    icon={selected.includes(option) ? faCheckSquare : faSquare}
                                    className="checkbox-icon"
                                />
                                <span>{option.toUpperCase()}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default MultiSelectDropdown;
